<template>
  <el-dialog
    :title="isEdit ? '编辑店铺' : '新增店铺'"
    :visible.sync="dialogVisible"
    width="600px"
    @close="handleClose"
  >
    <el-form
      ref="agentForm"
      :model="formData"
      :rules="rules"
      label-width="120px"
    >
      <!-- 店铺编码 -->
      <el-form-item label="店铺编码" prop="agent_code">
        <el-input
          v-model="formData.agent_code"
          placeholder="请输入店铺编码（如：AGENT001）"
          :disabled="isEdit"
          maxlength="50"
        />
        <div style="margin-top: 5px; font-size: 12px; color: #909399">
          建议格式：AGENT + 数字，只能包含大写字母和数字
        </div>
      </el-form-item>

      <!-- 店铺名称 -->
      <el-form-item label="店铺名称" prop="agent_name">
        <el-input
          v-model="formData.agent_name"
          placeholder="请输入店铺名称"
          maxlength="100"
          show-word-limit
        />
      </el-form-item>

      <!-- 联系人 -->
      <el-form-item label="联系人" prop="contact_person">
        <el-input
          v-model="formData.contact_person"
          placeholder="请输入联系人姓名"
          maxlength="50"
        />
      </el-form-item>

      <!-- 联系电话 -->
      <el-form-item label="联系电话" prop="contact_phone">
        <el-input
          v-model="formData.contact_phone"
          placeholder="请输入联系电话"
          maxlength="20"
        />
      </el-form-item>

      <!-- 联系邮箱 -->
      <el-form-item label="联系邮箱" prop="contact_email">
        <el-input
          v-model="formData.contact_email"
          placeholder="请输入联系邮箱（可选）"
          maxlength="100"
        />
      </el-form-item>

      <!-- 地址 -->
      <el-form-item label="地址" prop="address">
        <el-input
          v-model="formData.address"
          type="textarea"
          :rows="2"
          placeholder="请输入详细地址（可选）"
          maxlength="200"
          show-word-limit
        />
      </el-form-item>

      <!-- 描述 -->
      <el-form-item label="描述" prop="description">
        <el-input
          v-model="formData.description"
          type="textarea"
          :rows="3"
          placeholder="请输入店铺描述（可选）"
          maxlength="200"
          show-word-limit
        />
      </el-form-item>

      <!-- 店铺状态（仅新增时显示） -->
      <el-form-item v-if="!isEdit" label="店铺状态" prop="status">
        <el-radio-group v-model="formData.status">
          <el-radio :label="1">启用</el-radio>
          <el-radio :label="0">禁用</el-radio>
        </el-radio-group>
      </el-form-item>

      <el-alert
        v-if="isEdit"
        title="提示"
        type="warning"
        :closable="false"
        style="margin-top: 10px"
      >
        <div style="font-size: 12px; line-height: 1.8">
          1. 店铺编码创建后不可修改<br>
          2. 店铺状态请使用专用功能修改<br>
          3. 分佣配置请在列表页使用"分佣配置"功能管理
        </div>
      </el-alert>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="submitLoading" @click="handleSubmit">
        {{ isEdit ? '保存' : '创建' }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { createAgent, updateAgent } from '@/api/iccid-agent'

export default {
  name: 'AgentDialog',
  data() {
    const validateAgentCode = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入店铺编码'))
      } else if (!/^[A-Z0-9]+$/.test(value)) {
        callback(new Error('店铺编码只能包含大写字母和数字'))
      } else {
        callback()
      }
    }

    const validatePhone = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入联系电话'))
      } else if (!/^1[3-9]\d{9}$/.test(value) && !/^(\d{3,4}-?)?\d{7,8}$/.test(value)) {
        callback(new Error('请输入正确的手机号或固定电话'))
      } else {
        callback()
      }
    }

    const validateEmail = (rule, value, callback) => {
      if (!value) {
        callback()
        return
      }
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/
      if (!emailRegex.test(value)) {
        callback(new Error('请输入正确的邮箱地址'))
      } else {
        callback()
      }
    }

    return {
      dialogVisible: false,
      submitLoading: false,
      isEdit: false,
      formData: {
        id: null,
        agent_code: '',
        agent_name: '',
        contact_person: '',
        contact_phone: '',
        contact_email: '',
        address: '',
        description: '',
        status: 1
      },
      rules: {
        agent_code: [
          { required: true, validator: validateAgentCode, trigger: 'blur' }
        ],
        agent_name: [
          { required: true, message: '请输入店铺名称', trigger: 'blur' },
          { min: 2, max: 100, message: '店铺名称长度为2-100个字符', trigger: 'blur' }
        ],
        contact_person: [
          { required: true, message: '请输入联系人', trigger: 'blur' },
          { min: 2, max: 50, message: '联系人长度为2-50个字符', trigger: 'blur' }
        ],
        contact_phone: [
          { required: true, validator: validatePhone, trigger: 'blur' }
        ],
        contact_email: [
          { validator: validateEmail, trigger: 'blur' }
        ]
      }
    }
  },
  methods: {
    // 打开弹窗
    open(data) {
      this.dialogVisible = true
      this.isEdit = !!data

      if (data) {
        // 编辑模式
        this.formData = {
          id: data.id,
          agent_code: data.agent_code,
          agent_name: data.agent_name,
          contact_person: data.contact_person,
          contact_phone: data.contact_phone,
          contact_email: data.contact_email || '',
          address: data.address || '',
          description: data.description || '',
          status: data.status
        }
      } else {
        // 新增模式
        this.formData = {
          id: null,
          agent_code: '',
          agent_name: '',
          contact_person: '',
          contact_phone: '',
          contact_email: '',
          address: '',
          description: '',
          status: 1
        }
      }
    },

    // 关闭弹窗
    handleClose() {
      this.dialogVisible = false
      this.$refs.agentForm.resetFields()
    },

    // 提交表单
    async handleSubmit() {
      this.$refs.agentForm.validate(async valid => {
        if (!valid) return

        this.submitLoading = true
        try {
          if (this.isEdit) {
            // 编辑店铺
            await updateAgent({
              id: this.formData.id,
              agent_name: this.formData.agent_name,
              contact_person: this.formData.contact_person,
              contact_phone: this.formData.contact_phone,
              contact_email: this.formData.contact_email,
              address: this.formData.address,
              description: this.formData.description
            })
            this.$message.success('店铺更新成功')
          } else {
            // 创建店铺
            await createAgent({
              agent_code: this.formData.agent_code,
              agent_name: this.formData.agent_name,
              contact_person: this.formData.contact_person,
              contact_phone: this.formData.contact_phone,
              contact_email: this.formData.contact_email,
              address: this.formData.address,
              description: this.formData.description,
              status: this.formData.status
            })
            this.$message.success('店铺创建成功')
          }

          this.handleClose()
          this.$emit('success')
        } catch (error) {
          this.$message.error(error.message || (this.isEdit ? '更新失败' : '创建失败'))
        } finally {
          this.submitLoading = false
        }
      })
    }
  }
}
</script>

<style scoped>
.dialog-footer {
  text-align: right;
}
</style>
